import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { EventSettings, FaceMode, FacesSettings, Phase } from '@karozik/engine';
import { api, FACE_MODE_LABELS } from '@karozik/ui';
import { toLocalInput, type AdminEvent } from '../types';
import { Field } from './Events';

export function SettingsTab({ event, onChange }: { event: AdminEvent; onChange(e: AdminEvent): void }) {
  const [name, setName] = useState(event.name);
  const [startsAt, setStartsAt] = useState(toLocalInput(event.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInput(event.endsAt));
  const [s, setS] = useState<EventSettings>(event.settings);
  const [status, setStatus] = useState<string | null>(null);
  const navigate = useNavigate();
  const patch = <K extends keyof EventSettings>(k: K, v: EventSettings[K]) => setS((x) => ({ ...x, [k]: v }));
  const patchFaces = (p: Partial<FacesSettings>) => patch('faces', { ...s.faces, ...p });
  const patchPhase = (i: number, p: Partial<Phase>) => patch('phases', s.phases.map((ph, j) => (j === i ? { ...ph, ...p } : ph)));

  async function save() {
    setStatus('…');
    try {
      // Toggled live from the remote: never overwritten by a stale copy from this form.
      const { wifi: _wifi, bannedTrackIds: _banned, showLyrics: _lyrics, photoWall: _photoWall, ...settings } = s;
      onChange(
        await api<AdminEvent>(`/api/admin/events/${event.id}`, {
          method: 'PATCH',
          json: { name, startsAt: new Date(startsAt), endsAt: new Date(endsAt), settings },
        }),
      );
      setStatus('✓ Enregistré');
    } catch (e) {
      setStatus((e as Error).message);
    }
  }

  /** Opens or closes the party right away: only the dates are sent, the rest of the form stays as edited. */
  async function setDates(which: 'open' | 'close') {
    const now = Date.now();
    const question =
      which === 'open'
        ? 'Ouvrir la soirée maintenant ? Le QR code fonctionnera tout de suite.'
        : 'Fermer la soirée maintenant ? Les invités verront le récap.';
    if (!confirm(question)) return;
    const dates =
      which === 'open'
        ? { startsAt: now, endsAt: Math.max(event.endsAt, now + 6 * 3600_000) }
        : { startsAt: Math.min(event.startsAt, now - 60_000), endsAt: now };
    setStatus('…');
    try {
      const updated = await api<AdminEvent>(`/api/admin/events/${event.id}`, {
        method: 'PATCH',
        json: { startsAt: new Date(dates.startsAt), endsAt: new Date(dates.endsAt) },
      });
      onChange(updated);
      setStartsAt(toLocalInput(updated.startsAt));
      setEndsAt(toLocalInput(updated.endsAt));
      setStatus(which === 'open' ? '✓ Soirée ouverte' : '✓ Soirée fermée');
    } catch (e) {
      setStatus((e as Error).message);
    }
  }

  async function remove() {
    if (!confirm(`Supprimer « ${event.name} » ? Invités, propositions, votes, photos partagées et récap seront effacés. C’est définitif.`)) return;
    setStatus('…');
    try {
      await api(`/api/admin/events/${event.id}`, { method: 'DELETE' });
      navigate('/', { replace: true });
    } catch (e) {
      setStatus((e as Error).message);
    }
  }

  const start = new Date(startsAt).getTime();

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Soirée</h2>
        <Field label="Nom">
          <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ouverture" hint="Le QR code fonctionne à partir de là.">
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="input" />
          </Field>
          <Field label="Fermeture" hint="Ensuite : récap de la soirée.">
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="input" />
          </Field>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void setDates('open')} className="rounded-xl bg-surface-2 px-3 py-2 text-sm">
            Ouvrir maintenant
          </button>
          <button onClick={() => void setDates('close')} className="rounded-xl bg-surface-2 px-3 py-2 text-sm">
            Fermer maintenant
          </button>
        </div>
      </section>

      <section className="space-y-4 rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Règles du jeu</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Titres en attente / invité">
            <input type="number" min={1} max={10} value={s.maxPendingPerGuest} onChange={(e) => patch('maxPendingPerGuest', Number(e.target.value))} className="input" />
          </Field>
          <Field label="Durée max d’un titre (min)">
            <input type="number" min={1} max={20} value={Math.round(s.maxDurationMs / 60000)} onChange={(e) => patch('maxDurationMs', Number(e.target.value) * 60000)} className="input" />
          </Field>
          <Field label="Fondu entre titres (s)">
            <input type="number" min={0} max={12} step={0.5} value={s.fadeMs / 1000} onChange={(e) => patch('fadeMs', Math.round(Number(e.target.value) * 1000))} className="input" />
          </Field>
        </div>
        <Toggle label="Autoriser les titres explicites" checked={s.allowExplicit} onChange={(v) => patch('allowExplicit', v)} />
        <Toggle label="Vote du public pour passer un titre" checked={s.skipVote.enabled} onChange={(v) => patch('skipVote', { ...s.skipVote, enabled: v })} />
        {s.skipVote.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Part des connectés (%)">
              <input type="number" min={5} max={100} value={Math.round(s.skipVote.ratio * 100)} onChange={(e) => patch('skipVote', { ...s.skipVote, ratio: Number(e.target.value) / 100 })} className="input" />
            </Field>
            <Field label="Minimum de votes">
              <input type="number" min={1} max={50} value={s.skipVote.minVotes} onChange={(e) => patch('skipVote', { ...s.skipVote, minVotes: Number(e.target.value) })} className="input" />
            </Field>
          </div>
        )}
        <Field label="Code animateur (borne)" hint="4 à 8 chiffres. Vide = mode animateur désactivé.">
          <input
            inputMode="numeric"
            value={s.animatorPin ?? ''}
            onChange={(e) => patch('animatorPin', e.target.value.replace(/\D/g, '').slice(0, 8) || null)}
            className="input"
          />
        </Field>
      </section>

      <FacesSection faces={s.faces} onChange={patchFaces} />

      <section className="space-y-4 rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Micro</h2>
        <Toggle
          label="Baisser la musique automatiquement quand on parle au micro"
          checked={s.ducking.auto}
          onChange={(v) => patch('ducking', { ...s.ducking, auto: v })}
        />
        <p className="text-xs text-ink-faint">
          Le lecteur écoute l’entrée micro de la Focusrite (choisie sur l’écran du lecteur). Le bouton « Prise de parole » marche toujours, même sans cette option.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Volume musique pendant la parole (%)">
            <input type="number" min={0} max={100} value={Math.round(s.ducking.level * 100)} onChange={(e) => patch('ducking', { ...s.ducking, level: Number(e.target.value) / 100 })} className="input" />
          </Field>
          <Field label="Sensibilité (seuil)">
            <input type="number" min={0.005} max={0.5} step={0.005} value={s.ducking.threshold} onChange={(e) => patch('ducking', { ...s.ducking, threshold: Number(e.target.value) })} className="input" />
          </Field>
        </div>
      </section>

      <section className="space-y-3 rounded-3xl bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Déroulé de la soirée</h2>
        <p className="text-sm text-ink-dim">L’autopilote suit ces phases. Tu peux toujours en forcer une depuis l’onglet Direct.</p>
        {s.phases.map((p, i) => (
          <div key={p.id} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 rounded-2xl bg-surface-2 p-3">
            <span className="text-2xl">{p.emoji}</span>
            <input value={p.name} onChange={(e) => patchPhase(i, { name: e.target.value })} className="input py-1.5" />
            <label className="text-sm text-ink-dim">
              à{' '}
              <input
                type="time"
                value={toLocalInput(start + p.startOffsetMin * 60000).slice(11)}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date(start);
                  d.setHours(h!, m!, 0, 0);
                  if (d.getTime() < start) d.setDate(d.getDate() + 1);
                  patchPhase(i, { startOffsetMin: Math.round((d.getTime() - start) / 60000) });
                }}
                className="rounded-lg bg-surface px-2 py-1"
              />
            </label>
            <label className="text-sm text-ink-dim">
              énergie{' '}
              <select
                value={`${p.energy[0]}-${p.energy[1]}`}
                onChange={(e) => patchPhase(i, { energy: e.target.value.split('-').map(Number) as [number, number] })}
                className="rounded-lg bg-surface px-2 py-1"
              >
                {['1-2', '1-3', '2-3', '2-4', '3-4', '3-5', '4-5'].map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </label>
          </div>
        ))}
      </section>

      <section className="space-y-3 rounded-3xl border border-danger/40 bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Zone sensible</h2>
        <p className="text-sm text-ink-dim">Supprimer la soirée efface ses invités, leurs propositions, les votes, les photos partagées et le récap. Pense à télécharger les photos avant (onglet Photos). La musique s’arrête et tout le monde est déconnecté.</p>
        <button onClick={() => void remove()} className="rounded-xl bg-danger px-4 py-2 text-sm font-bold">
          Supprimer la soirée
        </button>
      </section>

      <div className="sticky bottom-4 flex items-center gap-3">
        <button onClick={() => void save()} className="bg-gradient-neon flex-1 rounded-2xl py-4 font-bold shadow-xl">
          Enregistrer
        </button>
        {status && <span className="text-sm text-ink-dim">{status}</span>}
      </div>
    </div>
  );
}

const FACE_MODE_HINTS: Record<FaceMode, string> = {
  blend: 'Les deux visages superposés à moitié.',
  split: 'Le haut de l’un, le bas de l’autre.',
  stripes: 'Des bandes verticales de chaque visage.',
  eyes: 'Le bandeau des yeux de l’autre.',
  morph: 'Un visage hybride : formes et traits à mi-chemin.',
  features: 'Les yeux, le nez et la bouche de l’autre, à la bonne place.',
  caricature: 'Le morphing, avec yeux, nez et bouche grossis.',
};
const WARPED: FaceMode[] = ['morph', 'features', 'caricature'];

/** Têtes Mêlées: which mixes are drawn, how long a round lasts, how hard the reveal is. */
function FacesSection({ faces, onChange }: { faces: FacesSettings; onChange(p: Partial<FacesSettings>): void }) {
  const toggle = (m: FaceMode, on: boolean) => {
    const modes = on ? [...faces.modes, m] : faces.modes.filter((x) => x !== m);
    if (modes.length > 0) onChange({ modes });
  };
  return (
    <section className="space-y-4 rounded-3xl bg-surface p-5">
      <h2 className="font-display text-lg font-bold">🤪 Têtes Mêlées</h2>
      <div>
        <h3 className="mb-2 text-xs font-bold tracking-widest text-ink-faint uppercase">Mélanges tirés au sort</h3>
        <ul className="space-y-2">
          {(Object.keys(FACE_MODE_HINTS) as FaceMode[]).map((m) => (
            <li key={m}>
              <label className="flex items-start gap-3 rounded-2xl bg-surface-2 p-3">
                <input
                  type="checkbox"
                  checked={faces.modes.includes(m)}
                  onChange={(e) => toggle(m, e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-pink"
                />
                <span className="min-w-0">
                  <span className="font-semibold">{FACE_MODE_LABELS[m]}</span>
                  {WARPED.includes(m) && <span className="ml-2 rounded-full bg-violet/30 px-2 py-0.5 text-xs text-ink-dim">✨ points du visage</span>}
                  <span className="block text-sm text-ink-dim">{FACE_MODE_HINTS[m]}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-ink-faint">
          Les modes ✨ déforment les visages grâce aux 468 points repérés sur le téléphone au moment du selfie (MediaPipe, sans rien envoyer ailleurs). Si un
          visage n’a pas été repéré, la manche prend un autre mode.
        </p>
      </div>
      <Slider
        label="Durée du dévoilement"
        value={faces.playSeconds}
        min={10}
        max={45}
        step={1}
        display={`${faces.playSeconds} s`}
        hint="Le temps que l’image met à s’affiner ; plus on répond tôt, plus on marque."
        onChange={(v) => onChange({ playSeconds: v })}
      />
      <Slider
        label="Dernière chance, image nette"
        value={faces.sharpSeconds}
        min={0}
        max={30}
        step={1}
        display={`+ ${faces.sharpSeconds} s`}
        hint="Une fois l’image complètement révélée, les joueurs ont encore ce temps pour répondre (une bonne réponse rapporte alors le minimum, 200 points)."
        onChange={(v) => onChange({ sharpSeconds: v })}
      />
      <Slider
        label="Difficulté du dévoilement"
        value={faces.pixelStart}
        min={12}
        max={60}
        step={2}
        display={`pixels de ${faces.pixelStart} px au départ`}
        hint="Plus les pixels de départ sont gros, plus c’est dur."
        onChange={(v) => onChange({ pixelStart: v })}
      />
      <Slider
        label="Dosage du morphing"
        value={Math.round(faces.morphRatio * 100)}
        min={20}
        max={80}
        step={5}
        display={`${100 - Math.round(faces.morphRatio * 100)} % / ${Math.round(faces.morphRatio * 100)} %`}
        hint="Part de chaque visage dans le morphing et la caricature. 50/50 est le plus dur à deviner."
        onChange={(v) => onChange({ morphRatio: v / 100 })}
      />
      <Slider
        label="Force de la caricature"
        value={faces.caricature}
        min={1}
        max={2.5}
        step={0.1}
        display={`× ${faces.caricature.toFixed(1).replace('.', ',')}`}
        hint="Agrandissement des yeux, du nez et de la bouche."
        onChange={(v) => onChange({ caricature: v })}
      />
    </section>
  );
}

function Slider(props: { label: string; value: number; min: number; max: number; step: number; display: string; hint: string; onChange(v: number): void }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span className="font-semibold">{props.label}</span>
        <span className="text-sm text-ink-dim tabular-nums">{props.display}</span>
      </span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
        className="mt-2 w-full accent-pink"
      />
      <span className="block text-xs text-ink-faint">{props.hint}</span>
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange(v: boolean): void }) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-6 w-6 accent-pink" />
    </label>
  );
}
